import {
  addDoc,
  arrayRemove,
  average,
  collection,
  collectionGroup,
  count,
  deleteField,
  doc,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  sum,
  updateDoc,
  where,
  writeBatch,
  arrayUnion
} from "firebase/firestore";
import { useEffect, useRef } from "react";
import { setAccountProfiles } from "../Redux/profile/action";
import { putSensorsData } from "../Redux/sensors/action";
import { setErrorStatus } from "../Redux/status/action";
import { putDevices, putReferencePicture } from "../Redux/surveillance/action";
import { setTracksData } from "../Redux/track/action";
import { addCaptureSummaryData } from "../Redux/workforce/action";
import { filesBucketFolderTypes } from "../Utils/Constants/Constants";
import { tracksQueryBuilder } from "../Utils/Constants/Tracks";
import {
  INEQUALITY_OPS,
  applyConstraints,
  parseTriplets,
  planQueries
} from "../Utils/queryPlanner";
import { database } from "./firebase";
import {
  deleteDocumentStorage,
  uploadReferencePicture,
  uploadReportFile
} from "./storage";

export function useCaptureSummaryListener(props) {
  const captureSummaryListener = useRef(null);

  const subscribeToCaptureSummary = () => {
    const collectionRef = collectionGroup(database, "captureSummary");

    return onSnapshot(
      collectionRef,
      (dataSnapshot) => {
        let captureSummary = {};
        dataSnapshot.forEach((doc) => {
          captureSummary[doc.data()?.location?.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });
        addCaptureSummaryData(captureSummary);
      },
      (error) => {
        console.error(error, "from capture summary listener");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (props.isAuth && captureSummaryListener.current === null) {
      captureSummaryListener.current = subscribeToCaptureSummary();
    } else if (
      props.isAuth === false &&
      typeof captureSummaryListener.current === "function"
    ) {
      captureSummaryListener.current();
      captureSummaryListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);
}

export function useDevicesListener(props) {
  const devicesListener = useRef(null);

  const subscribeToDevices = () => {
    const collectionRef = collection(database, "devices");

    return onSnapshot(
      collectionRef,
      (dataSnapshot) => {
        let devices = {};
        dataSnapshot.forEach((doc) => {
          devices[doc.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });
        putDevices(devices);
      },
      (error) => {
        console.error(error, "from devices listener");

        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (
      props.isAuth === true &&
      devicesListener.current === null
    ) {
      devicesListener.current = subscribeToDevices();
    } else if (
      props.isAuth === false &&
      typeof devicesListener.current === "function"
    ) {
      devicesListener.current();
      devicesListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);
}

export function usePicturesListener(props) {
  const picturesListener = useRef(null);

  const subscribeToPictures = () => {
    const collectionRef = collection(database, "pictures");

    return onSnapshot(
      collectionRef,
      (dataSnapshot) => {
        let pictures = {};
        dataSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.action === undefined) {
            pictures[doc.id] = {
              ...data,
              documentId: doc.id
            };
          }
        });
        putReferencePicture(pictures);
      },
      (error) => {
        console.error(error, "from pictures listener");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (
      props.isAuth &&
      picturesListener.current === null &&
      props.isAllowedToRead
    ) {
      picturesListener.current = subscribeToPictures();
    } else if (
      props.isAuth === false &&
      typeof picturesListener.current === "function"
    ) {
      picturesListener.current();
      picturesListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.isAllowedToRead]);
}

export function useProfileListener(props) {
  const profileListener = useRef(null);

  const subscribeToProfile = (contactInfo) => {
    const profileQuery = query(
      collection(database, "employees"),
      where(
        contactInfo.includes("@") ? "email" : "phoneNumber",
        "==",
        contactInfo
      )
    );

    return onSnapshot(
      profileQuery,
      (dataSnapshot) => {
        let profiles = {};
        dataSnapshot.forEach((doc) => {
          profiles = {
            ...doc.data()
          };
        });
        setAccountProfiles(profiles);
      },
      (error) => {
        console.error(error, "from profile");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (
      props.isAuth &&
      typeof props.contactInfo === "string" &&
      typeof props.uid === "string" &&
      profileListener.current === null
    ) {
      profileListener.current = subscribeToProfile(props.contactInfo);
    } else if (!props.isAuth && typeof profileListener.current === "function") {
      profileListener.current();
      profileListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.contactInfo]);
}

export function useSensorsListener(props) {
  const sensorsListener = useRef(null);

  const subscribeToSensors = () => {
    const collectionRef = collection(database, "sensors");

    return onSnapshot(
      collectionRef,
      (dataSnapshot) => {
        let sensors = {};
        dataSnapshot.forEach((doc) => {
          sensors[doc.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });
        putSensorsData(sensors);
      },
      (error) => {
        console.error(error, "from sensors listener");

        setErrorStatus(error);
      }
    );
  };
  useEffect(() => {
    if (props.isAuth === true && sensorsListener.current === null) {
      sensorsListener.current = subscribeToSensors();
    } else if (
      props.isAuth === false &&
      typeof sensorsListener.current === "function"
    ) {
      sensorsListener.current();
      sensorsListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);
}

export function useTracksListener(props) {
  const tracksListener = useRef(null);
  const filterQuery = useRef([]);
  const listeningFilterData = useRef([]);

  useEffect(() => {
    const query = tracksQueryBuilder(props.filterData);
    filterQuery.current = query;

    if (
      typeof tracksListener.current === "function" &&
      props.isAuth &&
      JSON.stringify(listeningFilterData.current) !==
        JSON.stringify(filterQuery.current)
    ) {
      listeningFilterData.current = filterQuery.current;
      tracksListener.current();
      tracksListener.current = subscribeToTracks();
    }
    // eslint-disable-next-line
  }, [props.filterData]);

  const subscribeToTracks = () => {
    let queryRef = query(
      collection(database, "tracks"),
      orderBy("timestamp", "desc")
    );

    if (Array.isArray(filterQuery.current)) {
      filterQuery.current?.forEach((value, index) => {
        if ((index + 1) % 3 === 0 && index !== 0) {
          queryRef = query(
            queryRef,
            where(
              filterQuery.current[index - 2],
              filterQuery.current[index - 1],
              value
            )
          );
        }
      });
    }

    return onSnapshot(
      queryRef,
      (dataSnapshot) => {
        let tracks = {};
        dataSnapshot.forEach((doc) => {
          const data = doc.data();
          const phoneNumber = data.phoneNumber.replace(/\+91/gi, "");
          if (!tracks[phoneNumber]) {
            tracks[phoneNumber] = {
              tracks: [data]
            };
          } else {
            tracks[phoneNumber]?.tracks?.push(data);
          }
        });
        setTracksData(tracks);
      },
      (error) => {
        console.error(error, "from profile");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (props.isAuth === true && tracksListener.current === null) {
      tracksListener.current = subscribeToTracks();
    } else if (!props.isAuth && typeof tracksListener.current === "function") {
      tracksListener.current();
      tracksListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);
}

export async function addRolesToWorkforce(documentId, data) {
  await updateDoc(doc(database, "employees", documentId), {
    roles: data
  });
}

export const assignProcedureToSensor = async (documentId, data) => {
  await updateDoc(doc(database, "sensors", documentId), {
    linkedProcedures: data
  });
};

export const updateConnectedLocation = async (employeeId, data) => {
  await updateDoc(doc(database, "employees", employeeId), {
    connected: data
  });
};

export const assignProcedureToWorkforce = async (employeeId, data) => {
  await updateDoc(doc(database, "employees", employeeId), {
    ...data
  });
};

export const createLocation = async (data) => {
  await addDoc(collection(database, "locations"), { ...data });
};

export async function createNewDevice(data) {
  const devicesRef = collection(database, "devices");
  await addDoc(devicesRef, data);
}

export async function createNewPicture(data, asset) {
  let assetPath;
  try {
    const pictureRef = doc(collection(database, "pictures"));
    assetPath = await uploadReferencePicture(asset, `${pictureRef.id}.jpg`);
    const date = +new Date();
    await setDoc(pictureRef, {
      ...data,
      asset: assetPath,
      updatedAt: date,
      lastAppliedAt: date,
      vmSync: false
    });
  } catch (error) {
    if (assetPath) {
      console.error(
        "error occurred when uploading picture , so deleting the file in",
        assetPath
      );
      await deleteDocumentStorage(assetPath);
    }
    throw error;
  }
}

export const createNewSensor = async (data) => {
  await addDoc(collection(database, "sensors"), { name: data });
};

export const createNewWorkforce = async (data) => {
  const employeeRef = doc(database, "employees", data.employeeId);
  await setDoc(employeeRef, {
    ...data
  });
};

export const createProcedurePreset = async (data) => {
  await addDoc(collection(database, "procedures"), { ...data });
};

export const deleteLinkedProcedures = async (documentId, data) => {
  await updateDoc(doc(database, "employees", documentId), {
    linkedProcedures: data
  });
};

export const deleteLinkedSensorProcedures = async (documentId, data) => {
  await updateDoc(doc(database, "sensors", documentId), {
    linkedProcedures: arrayRemove(data)
  });
};

export const editProcedurePreset = async (documentId, data) => {
  await updateDoc(doc(database, "procedures", documentId), data);
};

export async function editPictureAction(documentId, action) {
  await updateDoc(doc(database, "pictures", documentId), {
    action: action,
    vmSync: false
  });
}

export async function getAveragePercentageOfWorkforceSummary(
  employeeId,
  dateRange
) {
  const { from, to, type } = dateRange;
  const q = query(
    collectionGroup(database, "attendanceSummary"),
    where("employee.id", "==", employeeId),
    ...getFilters(type, from, to),
    orderBy("timestamp"),
    orderBy("percentage")
  );

  const aggregateResult = await getAggregateFromServer(q, {
    totalPercentage: sum("percentage"),
    count: count()
  });

  const totalPercentage = aggregateResult.data().totalPercentage || 0;
  const countValue = aggregateResult.data().count || 0;

  return {
    [employeeId]: Math.ceil(countValue > 0 ? totalPercentage / countValue : 0)
  };
}

export async function getAveragePercentageOfWorkforceSummaryForEachRoles(
  roleId,
  dateRange
) {
  try {
    const { from, to, type } = dateRange;

    const percentageQuery = query(
      collectionGroup(database, "attendanceSummary"),
      where("roleId", "array-contains-any", [roleId]),
      ...getFilters(type, from, to),
      orderBy("timestamp"),
      orderBy("percentage")
    );
    const fetchCount = type === "Today" || from === to;
    let countQuery;

    if (fetchCount) {
      countQuery = query(
        collectionGroup(database, "attendanceSummary"),
        where("roleId", "array-contains-any", [roleId]),
        where("percentage", ">", 0),
        orderBy("percentage"),
        ...getFilters(type, from, to),
        orderBy("timestamp")
      );
    }

    const [percentageResult, countResult] = await Promise.all([
      getAggregateFromServer(percentageQuery, {
        totalPercentage: average("percentage")
      }),
      ...(fetchCount
        ? [getAggregateFromServer(countQuery, { count: count() })]
        : [])
    ]);

    const present = Math.ceil(percentageResult?.data()?.totalPercentage || 0);

    return {
      [roleId]: {
        present,
        absent: 100 - present,
        ...(fetchCount
          ? { totalPresent: Math.ceil(countResult?.data()?.count || 0) }
          : {})
      }
    };
  } catch (error) {
    console.error(
      error,
      "from getAveragePercentageOfWorkforceSummaryForEachRoles"
    );
  }
}

export async function getAveragePercentageOfTotalWorkforceSummary(dateRange) {
  try {
    const { from, to, type } = dateRange;

    const percentageQuery = query(
      collectionGroup(database, "attendanceSummary"),
      ...getFilters(type, from, to),
      orderBy("timestamp"),
      orderBy("percentage")
    );

    const fetchCount = type === "Today" || from === to;
    let countQuery;

    if (fetchCount) {
      countQuery = query(
        collectionGroup(database, "attendanceSummary"),
        where("percentage", ">", 0),
        orderBy("percentage"),
        ...getFilters(type, from, to),
        orderBy("timestamp")
      );
    }

    const [percentageResult, countResult] = await Promise.all([
      getAggregateFromServer(percentageQuery, {
        totalPercentage: average("percentage")
      }),
      ...(fetchCount
        ? [getAggregateFromServer(countQuery, { count: count() })]
        : [])
    ]);
    const present = Math.ceil(percentageResult.data()?.totalPercentage) || 0;

    return {
      present,
      absent: 100 - present,
      ...(fetchCount
        ? { totalPresent: Math.ceil(countResult.data()?.count) || 0 }
        : {})
    };
  } catch (error) {
    console.error(error, "from getAveragePercentageOfTotalWorkforceSummary");
  }
}
// Runs one getCountFromServer per fan-out plan (see Utils/queryPlanner) and sums the
// results. Plans partition the document set, so the sum is exact.
async function countWithFanOut(collectionRef, constraints) {
  const plans = planQueries(constraints);
  const snapshots = await Promise.all(
    plans.map((plan) => getCountFromServer(applyConstraints(collectionRef, plan)))
  );
  return snapshots.reduce((total, snapshot) => total + snapshot.data().count, 0);
}

// Same idea as countWithFanOut, but for an average: averaging per-plan averages would
// be wrong (plans can have different sizes), so sum(total)/sum(n) across plans instead.
async function averageWithFanOut(collectionRef, constraints, field) {
  const inequalityFields = [
    ...new Set(
      constraints
        .filter(({ operator }) => INEQUALITY_OPS.includes(operator))
        .map((c) => c.field)
    )
  ];
  // Firestore requires any orderBy to start with the inequality-filtered field(s), in
  // filter order, before any other orderBy. orderBy(field) also has the side effect we
  // rely on: it excludes documents where `field` doesn't exist (e.g. still-open
  // complaints with no closedDuration), matching this function's original behaviour.
  const orderFields = [...new Set([...inequalityFields, field])];

  const plans = planQueries(constraints);
  const aggregates = await Promise.all(
    plans.map((plan) => {
      let q = applyConstraints(collectionRef, plan);
      orderFields.forEach((f) => {
        q = query(q, orderBy(f));
      });
      return getAggregateFromServer(q, { total: sum(field), n: count() });
    })
  );

  let total = 0;
  let n = 0;
  aggregates.forEach((snapshot) => {
    total += snapshot.data().total || 0;
    n += snapshot.data().n || 0;
  });

  return n > 0 ? total / n : null;
}

export async function getAverageDurationOfClosedComplaints(filters) {
  const constraints = parseTriplets(filters);
  return averageWithFanOut(
    collection(database, "complaints"),
    constraints,
    "closedDuration"
  );
}

export async function getComplaint(complaintId) {
  const docRef = doc(database, "complaints", complaintId);
  const docSnapshot = await getDoc(docRef);

  if (!docSnapshot.exists()) {
    return {};
  }

  return {
    documentId: docSnapshot.id,
    ...docSnapshot.data()
  };
}

export async function getComplaintsSummary(filters, order, resultLimit) {
  const constraints = parseTriplets(filters);
  const inequalityFields = [
    ...new Set(
      constraints
        .filter(({ operator }) => INEQUALITY_OPS.includes(operator))
        .map((c) => c.field)
    )
  ];

  const complaintsRef = collection(database, "complaints");
  const plans = planQueries(constraints);

  const snapshots = await Promise.all(
    plans.map((plan) => {
      let q = applyConstraints(complaintsRef, plan);

      inequalityFields.forEach((field) => {
        q = query(q, orderBy(field));
      });

      if (order && !inequalityFields.includes(order.field)) {
        q = query(q, orderBy(order.field, order.direction));
      }

      if (resultLimit) {
        q = query(q, limit(resultLimit));
      }

      return getDocs(q);
    })
  );

  let complaints = [];
  snapshots.forEach((snapshot) => {
    snapshot.forEach((doc) => {
      complaints.push({
        ...doc.data(),
        documentId: doc.id
      });
    });
  });

  // Plans run independently (fan-out over a chunked "in" filter), so a per-plan
  // limit() only bounds each chunk individually — re-sort and re-slice the merged
  // set to get the true top-N across all plans.
  if (plans.length > 1 && order) {
    complaints.sort((a, b) => {
      const cmp =
        a[order.field] > b[order.field] ? 1 : a[order.field] < b[order.field] ? -1 : 0;
      return order.direction === "desc" ? -cmp : cmp;
    });
  }

  if (plans.length > 1 && resultLimit) {
    complaints = complaints.slice(0, resultLimit);
  }

  return complaints;
}

export async function getComplaintsSummaryCount(
  filters,
  accessToken,
  collectionName
) {
  const complaintsRef = collection(database, collectionName || "complaints");
  const constraints = parseTriplets(filters);
  return countWithFanOut(complaintsRef, constraints);
}

export async function getComplaintsTotalCount(props) {
  const constraints = parseTriplets(props.filters);
  return countWithFanOut(collection(database, "complaints"), constraints);
}

export async function getComplaintSummary() {
  const querySnapshot = await getDocs(
    collectionGroup(database, "complaintSummary")
  );

  const complaints = [];
  querySnapshot.forEach((doc) => {
    complaints.push({ documentId: doc.id, ...doc.data() });
  });

  return complaints;
}

export async function getEmployeeCaptures(employeeId, dateRange) {
  const capturesRef = query(
    collection(database, "captures"),
    where("employee.id", "==", employeeId),
    where("createdAt", ">=", +new Date(`${dateRange.from}:00:00:00`)),
    where("createdAt", "<=", +new Date(`${dateRange.to}:23:59:59`)),
    where("success", "==", true),
    orderBy("createdAt", "desc")
  );

  const capturesSnapshot = await getDocs(capturesRef);

  return capturesSnapshot.docs.reduce((acc, doc) => {
    acc[doc.id] = { ...doc.data(), documentId: doc.id };
    return acc;
  }, {});
}

export async function getPendingEmployeeCaptures(employeeId) {
  const capturesRef = query(
    collection(database, "captures"),
    where("employee.id", "==", employeeId),
    where("approval.status", "==", "PENDING"),
    orderBy("createdAt", "desc")
  );

  const capturesSnapshot = await getDocs(capturesRef);

  return capturesSnapshot.docs.reduce((acc, doc) => {
    acc[doc.id] = { ...doc.data(), documentId: doc.id };
    return acc;
  }, {});
}

export async function getLastCaptures(employeeId, count) {
  const capturesRef = query(
    collection(database, "captures"),
    where("employee.id", "==", employeeId),
    where("success", "==", true),
    limit(count)
  );

  const capturesSnapshot = await getDocs(capturesRef);

  return capturesSnapshot.docs.map((doc) => {
    return { ...doc.data(), documentId: doc.id };
  });
}

export async function getPictureStatusData(pictureId) {
  const statusRef = query(
    collectionGroup(database, "status"),
    where("pictureId", "==", pictureId)
  );
  let status = {};
  const employeeSnapshot = await getDocs(statusRef);
  employeeSnapshot.forEach((doc) => {
    status[doc.id] = { ...doc.data(), documentId: doc.id };
  });
  return status;
}

export async function getProcedureHistory(locationId) {
  const procedureHistoryQuery = query(
    collectionGroup(database, "procedureHistory"),
    where("locationId", "==", locationId)
  );
  const querySnapshot = await getDocs(procedureHistoryQuery);
  const procedureHistory = [];
  querySnapshot.forEach((doc) => {
    procedureHistory.push({ documentId: doc.id, ...doc.data() });
  });
  return procedureHistory;
}

export async function getProcedureHistoryTotalCount(props) {
  let complaintsRef = query(collectionGroup(database, "procedureHistory"));

  if (props.filters.length > 0) {
    props.filters?.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        complaintsRef = query(
          complaintsRef,
          where(props.filters[index - 2], props.filters[index - 1], value)
        );
      }
    });
  }

  const count = await getCountFromServer(complaintsRef);
  return count.data().count;
}

export async function getRolesData() {
  const rolesRef = query(collection(database, "roles"));
  let roles = {};
  const rolesSnapshot = await getDocs(rolesRef);
  rolesSnapshot.forEach((doc) => {
    roles[doc.id] = {
      ...doc.data(),
      documentId: doc.id
    };
  });
  return roles;
}

export async function reloadPictureUpload(documentId) {
  await updateDoc(doc(database, "pictures", documentId), {
    lastAppliedAt: +new Date()
  });
}

export async function updateCompliant(compliantId, data) {
  const rootRef = doc(database, "complaints", compliantId);

  await updateDoc(rootRef, {
    ...data,
    ...(data.status?.currentStatus === "OPEN"
      ? { closure: deleteField() }
      : {}),
    ...(data.latestUpdate
      ? { latestUpdate: arrayUnion(data.latestUpdate) }
      : {})
  });
}

export const updateWorkforce = async (documentId, profileData) => {
  await updateDoc(doc(database, "employees", documentId), profileData);
};

export const unarchiveWorkforce = async (documentId) => {
  await updateDoc(doc(database, "employees", documentId), {
    "actions.deletedAt": deleteField()
  });
};

export async function updateCapturedPictureApproval(documentId, data) {
  await updateDoc(doc(database, "captures", documentId), data);
}

// to find why listener trigged

// dataSnapshot.docChanges().forEach((change) => {
//   const doc = change.doc;
//   if (change.type === "added") {
//     console.log(`Document added: ${doc.id}`, doc.data());
//   }
//   if (change.type === "modified") {
//     console.log(`Document modified: ${doc.id}`, doc.data());
//   }
//   if (change.type === "removed") {
//     console.log(`Document removed: ${doc.id}`);
//   }
// });

// export async function createNewProfilePicture(employeeId, asset) {
//   const employeeRef = doc(database, "employees", employeeId);
//   const assetPath = await uploadProfilePicture(asset, `${employeeId}.jpg`);
//   const date = +new Date();
//   await updateDoc(employeeRef, {
//     picture: {
//       asset: assetPath,
//       updatedAt: date,
//       lastAppliedAt: date,
//     }
// });
// }

const getFilters = (type, from, to) => {
  if (type === "Lifetime") return [];

  const startOfDay = new Date(from).setHours(0, 0, 0, 0);
  const endOfDay = new Date(to).setHours(0, 0, 0, 0);
  return type === "Today" || from === to
    ? [where("timestamp", "==", startOfDay)]
    : [
        where("timestamp", ">=", startOfDay),
        where("timestamp", "<=", endOfDay)
      ];
};

export async function createNewfile(data, asset, fileName) {
  let assetPath;
  try {
    const fileRef = doc(collection(database, "files"));
    assetPath = await uploadReportFile(
      asset,
      `/files/${filesBucketFolderTypes[data.type]}/${fileRef.id}/${fileName}`
    );
    await setDoc(fileRef, {
      ...data,
      asset: assetPath
    });
  } catch (error) {
    if (assetPath) {
      console.error(
        "error occurred when uploading file , so deleting the file in",
        assetPath
      );
      await deleteDocumentStorage(assetPath);
    }
    throw error;
  }
}

export const updateNotification = async (ruleId, notificationId, data) => {
  const notificationRef = doc(
    database,
    "notificationRules",
    ruleId,
    "notifications",
    notificationId
  );
  await updateDoc(notificationRef, data);

  return { success: true };
};

export async function readAllNotifications(userUid) {
  try {
    const notificationsQuery = query(
      collectionGroup(database, "notifications"),
      where("user.uid", "==", userUid),
      where("read", "==", false)
    );

    const snapshot = await getDocs(notificationsQuery);

    let batch = writeBatch(database);
    let documentCount = 0;

    for (const docSnap of snapshot.docs) {
      const docRef = doc(database, docSnap.ref.path);

      batch.update(docRef, { read: true });
      documentCount++;

      if (documentCount === 500) {
        await batch.commit();
        batch = writeBatch(database);
        documentCount = 0;
      }
    }

    if (documentCount > 0) {
      await batch.commit();
    }

    return { success: true };
  } catch (error) {
    console.error("Failed to mark all notifications as read:", error);
    return { success: false, error };
  }
}

export async function fetchUnreadCount(uid) {
  const q = query(
    collectionGroup(database, "notifications"),
    where("user.uid", "==", uid),
    where("archived", "==", false),
    where("read", "==", false)
  );

  const count = await getCountFromServer(q);
  return count.data().count;
}

export async function updateLocationImageUrl(locationId, imageUrls) {
  const locationRef = doc(database, "locations", locationId);
  await updateDoc(locationRef, {
    assets: imageUrls
  });
}

export async function getComplaintsByLocationId(locationId) {
  const complaintsQuery = query(
    collection(database, "complaints"),
    where("location.id", "==", locationId),
    orderBy("createdAt", "desc"),
    limit(10)
  );

  const complaintsSnapshot = await getDocs(complaintsQuery);
  const complaints = {};

  complaintsSnapshot.forEach((doc) => {
    complaints[doc.id] = {
      ...doc.data(),
      documentId: doc.id
    };
  });

  return complaints;
}

export async function getProcedureHistoryByLocationId(locationId) {
  const procedureHistoryQuery = query(
    collectionGroup(database, "procedureHistory"),
    where("locationId", "==", locationId),
    orderBy("createdAt", "desc"),
    limit(10)
  );

  const procedureHistorySnapshot = await getDocs(procedureHistoryQuery);
  const procedureHistory = {};

  procedureHistorySnapshot.forEach((doc) => {
    procedureHistory[doc.id] = {
      ...doc.data(),
      documentId: doc.id
    };
  });

  return procedureHistory;
}

export async function getWorkforceByLocationId(locationId) {
  const workforceQuery = query(
    collection(database, "employees"),
    where("connected.location", "array-contains", locationId)
  );

  const workforceSnapshot = await getDocs(workforceQuery);
  const workforce = {};

  workforceSnapshot.forEach((doc) => {
    workforce[doc.id] = {
      ...doc.data(),
      documentId: doc.id
    };
  });

  return workforce;
}

export async function getEmployeeCapturesByLocationId(locationId, dateRange) {
  const capturesRef = query(
    collection(database, "captures"),
    where("location.id", "==", locationId),
    where("createdAt", ">=", dateRange.from),
    where("createdAt", "<=", dateRange.to),
    where("success", "==", true),
    orderBy("createdAt", "desc")
  );

  const capturesSnapshot = await getDocs(capturesRef);
  const captures = {};
  capturesSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    captures[doc.id] = {
      ...data,
      documentId: doc.id
    };
  });
  return captures;
}

export async function getOperations() {
  const docRef = doc(database, "config", "operations");
  const docSnapshot = await getDoc(docRef);

  if (!docSnapshot.exists()) {
    return {};
  }

  const data = {
    documentId: docSnapshot.id,
    ...docSnapshot.data()
  };

  return data;
}

export async function updatelocationStatusinDB(locationId, status, reason) {
  const locationRef = doc(database, "locations", locationId);
  await updateDoc(locationRef, {
    operationalStatus: {
      state: status,
      reason: status === "Inactive" ? reason : ""
    }
  });
  return true;
}